import { randomId } from '../../utils/randomId';
import { useCallback, useEffect, useRef, useState } from 'react';
import { makePlayerReadyEvent, makeOrderFinalizedEvent } from '../../domain/events';
import { shuffleIds } from '../../domain/shuffle';
import { eventStore } from '../../services/EventStore';
import { clearReservedPlayerId, clearSessionPlayerId, getReservedPlayerId, getSessionPlayerId, setReservedPlayerId } from '../../utils/sessionPlayer';
import { readSession } from '../../utils/sessionContract';
import { GameStartedGameEvent, GameState } from '../../types/game';
import { GameAction } from '../gameReducer';
import { isDevEnv } from '../../utils/debugFlags';
import { readDevPlayAsSpectatorPreference } from '../../utils/devSpectatorMode';

interface UseOnlineLobbyDeps {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  setIsHost: (v: boolean) => void;
  setCurrentClientPlayerId: (id: string | null) => void;
  currentClientPlayerId: string | null;
}

type InitLobbyOptions = {
  gameId?: string;
  groupToken?: string | null;
  groupId?: string | null;
  assumeHost?: boolean;
};

/**
 * Zarządza fazą lobby: ready debounce, finalize order, begin game.
 * Wyciągnięte z GameContext dla separacji odpowiedzialności.
 */
export function resolveFirstPlayerIdForStart(
  order: string[],
  options: {
    currentClientPlayerId?: string | null;
    sessionPlayerId?: string | null;
    isDev?: boolean;
    devPlayAsSpectator?: boolean;
  },
): string | null {
  const first = order[0] || null;
  if (!first) return null;
  if (!options.isDev || !options.devPlayAsSpectator) return first;
  const localPlayerIds = [options.currentClientPlayerId, options.sessionPlayerId].filter(
    (value): value is string => Boolean(value),
  );
  if (localPlayerIds.length === 0) return first;
  if (!localPlayerIds.includes(first)) return first;
  return order.find((id) => !localPlayerIds.includes(id)) || first;
}

export function useOnlineLobby({ gameState, dispatch, setIsHost, setCurrentClientPlayerId, currentClientPlayerId }: UseOnlineLobbyDeps) {
  const [pendingReadyChanges, setPendingReadyChanges] = useState<Set<string>>(new Set());
  const readyDebounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const gameStateRef = useRef(gameState);
  const playersRef = useRef(gameState.players);

  useEffect(() => {
    gameStateRef.current = gameState;
    playersRef.current = gameState.players;
  }, [gameState]);

  useEffect(() => {
    const timers = readyDebounceTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [gameState.id]);

  const pickFirstPlayerIdForStart = useCallback((order: string[]): string | null => {
    return resolveFirstPlayerIdForStart(order, {
      currentClientPlayerId,
      sessionPlayerId: getSessionPlayerId(),
      isDev: isDevEnv(),
      devPlayAsSpectator: readDevPlayAsSpectatorPreference(),
    });
  }, [currentClientPlayerId]);

  const initOnlineLobby = useCallback((options?: InitLobbyOptions) => {
    const nextRoomId = options?.gameId?.trim().toUpperCase() || null;
    dispatch({
      type: 'INIT_ONLINE_LOBBY',
      gameId: nextRoomId || undefined,
      groupToken: options?.groupToken ?? null,
      groupId: options?.groupId ?? null,
    });
    const sessionPid = getSessionPlayerId();
    const session = readSession();
    let nextHost = typeof options?.assumeHost === 'boolean' ? options.assumeHost : true;
    if (session?.mode === 'ONLINE' && typeof options?.assumeHost !== 'boolean') {
      nextHost = session.role === 'host';
    } else if ((gameState.players.length > 0 || sessionPid) && typeof options?.assumeHost !== 'boolean') {
      nextHost = false;
    }
    setIsHost(nextHost);
    if (nextHost && !sessionPid && nextRoomId) {
      clearReservedPlayerId();
      if (!getReservedPlayerId(nextRoomId)) {
        setReservedPlayerId(nextRoomId, randomId());
      }
    }
    if (nextHost && !sessionPid) {
      clearSessionPlayerId();
      setCurrentClientPlayerId(null);
    }
    try { localStorage.setItem('dice1k-mode', 'ONLINE'); } catch {}
  }, [dispatch, setIsHost, setCurrentClientPlayerId, gameState.players.length]);

  const finalizeOrder = useCallback(() => {
    const gs = gameStateRef.current;
    if (gs.mode === 'REAL' || !gs.lobby) return;
    // Jeśli już jesteśmy w ORDER_REVEAL i mamy kolejność -> drugie wywołanie = start gry.
    if (gs.lobby.phase === 'ORDER_REVEAL' && gs.lobby.order?.length) {
      const first = pickFirstPlayerIdForStart(gs.lobby.order);
      if (!first) return;
      const eventId = randomId();
      const started: GameStartedGameEvent = {
        id: eventId,
        type: 'GAME_STARTED',
        turn: 0,
        timestamp: new Date().toISOString(),
        payload: { firstPlayerId: first, eventId },
      };
      void eventStore.appendEvent(gs.id, started);
      return;
    }
    const ids = (playersRef.current || []).map(p => p.id);
    const order = shuffleIds(ids);
    void eventStore.appendEvent(gs.id, makeOrderFinalizedEvent(order));
  }, [pickFirstPlayerIdForStart]);

  const beginOnlineGame = useCallback(() => {
    const gs = gameStateRef.current;
    if (gs.mode === 'REAL' || !gs.lobby || gs.lobby.phase !== 'ORDER_REVEAL' || !gs.lobby.order?.length) return;
    const first = pickFirstPlayerIdForStart(gs.lobby.order);
    if (!first) return;
    // Wyślij GAME_STARTED jako jednoznaczny trigger do wszystkich klientów.
    const eventId = randomId();
    const ev: GameStartedGameEvent = {
      id: eventId,
      type: 'GAME_STARTED',
      turn: 0,
      timestamp: new Date().toISOString(),
      payload: { firstPlayerId: first, eventId },
    };
    void eventStore.appendEvent(gs.id, ev);
  }, [pickFirstPlayerIdForStart]);

  const toggleReady = useCallback(async (playerId: string) => {
    if (readyDebounceTimers.current.has(playerId)) return;
    const currentReady = gameState.lobby?.readyMap[playerId] ?? false;
    const newReady = !currentReady;

    const existing = readyDebounceTimers.current.get(playerId);
    if (existing) {
      clearTimeout(existing);
      readyDebounceTimers.current.delete(playerId);
    }

    setPendingReadyChanges(prev => new Set(prev).add(playerId));

    const timer = setTimeout(async () => {
      readyDebounceTimers.current.delete(playerId);
      try {
        const ev = makePlayerReadyEvent(playerId, newReady);
        await eventStore.appendEvent(gameState.id, ev);
      } catch {
        // Do nothing: local state remains server-authoritative.
      } finally {
        setPendingReadyChanges(prev => {
          const ns = new Set(prev);
            ns.delete(playerId);
            return ns;
        });
      }
    }, 100);
    readyDebounceTimers.current.set(playerId, timer);
  }, [gameState.lobby, gameState.id]);

  const clearPendingReadyChange = useCallback((playerId: string) => {
    setPendingReadyChanges((prev) => {
      if (!prev.has(playerId)) return prev;
      const next = new Set(prev);
      next.delete(playerId);
      return next;
    });
  }, []);

  return {
    // actions
    toggleReady,
    initOnlineLobby,
    finalizeOrder,
    beginOnlineGame,
    clearPendingReadyChange,
    // state
    pendingReadyChanges,
  };
}

